import { useState, useEffect } from "react";
import { Sentry } from "@/lib/sentry";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CheckCircle, XCircle, Truck, Calendar, Weight, MapPin, Building2, Minus, Plus, Route, Clock, RefreshCw, ChevronDown } from "lucide-react";
import { formatAppDate } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useRouteDisplay } from '@/utils/routeUtils';
// Notification will be automatically queued by database trigger

interface LoadDetail {
  pickupCity: string;
  dropCity: string;
  pickupDate: string;
  vehicleType: string;
  weightTons: number;
  cargo_desc?: string;
  truckCount?: number;
  trucksNeeded?: number;
  distance?: string;
  estimatedDuration?: string;
}

interface AtomicAcceptanceResponse {
  success: boolean;
  error?: string;
  trucks_requested?: number;
  trucks_available?: number;
  trucks_assigned?: number;
  partial_available?: number;
  assignment_id?: string;
  message?: string;
}

interface AcceptanceRecord {
  id: string;
  trucksAccepted: number;
  timestamp: Date;
  status: string;
}

interface LoadAcceptanceCardProps {
  id: string;
  title: string;
  subtitle?: string;
  loadDetail: LoadDetail;
  totalTrucks: number;
  assignedTrucks: number;
  availableTrucks: number;
  trucksCompleted?: number;
  trucksCancelled?: number;
  hasBeenAccepted?: boolean;
  currentResponse?: {
    trucksCommitted: number;
    status: "selecting" | "accepted";
  };
  onStartSelection: (id: string) => void;
  onConfirmAcceptance: (id: string) => void;
  onUpdateTrucks: (id: string, trucks: number) => void;
  onCancelSelection: (id: string) => void;
  badges?: Array<{
    text: string;
    variant: "default" | "secondary" | "outline" | "destructive";
    className?: string;
    icon?: React.ReactNode;
    animate?: boolean;
  }>;
  showExpandedAcceptances?: boolean;
  onToggleExpandedAcceptances?: () => void;
  additionalDetails?: React.ReactNode;
  status?: "available" | "partially_assigned" | "assigned" | "in_transit" | "delivered";
  context: "accept-load" | "view-order";
  onComplete?: (id: string, count: number) => void;
  onCancel?: (id: string, count: number) => void;
  isLoadingComplete?: boolean;
  isLoadingCancel?: boolean;
  isLoadingAccept?: boolean;
  // Simple boolean props to control button visibility
  showAcceptButton?: boolean;
  showCompleteButton?: boolean;
  showCancelButton?: boolean;
  showAvailableLabel?: boolean;
  showMyAssignments?: boolean;
  showRouteAvailabilityBar?: boolean;
  showRouteAssignedStatusBar?: boolean;
  showCarrierRouteStatusBar?: boolean;
  showFullyAssignedLoadBadge?: boolean;
  showActionButton?: boolean;
  onRouteDataUpdate?: () => void;
}

export const LoadAcceptanceCard = ({
  id,
  title,
  subtitle,
  loadDetail,
  totalTrucks,
  assignedTrucks,
  availableTrucks,
  trucksCompleted = 0,
  trucksCancelled = 0,
  hasBeenAccepted = false,
  currentResponse,
  onStartSelection,
  onConfirmAcceptance,
  onUpdateTrucks,
  onCancelSelection,
  badges = [],
  showExpandedAcceptances = false,
  onToggleExpandedAcceptances,
  additionalDetails,
  status,
  context,
  onComplete,
  onCancel,
  isLoadingComplete = false,
  isLoadingCancel = false,
  isLoadingAccept = false,
  // Simple boolean props to control button visibility with defaults
  showAcceptButton = true,
  showCompleteButton = true,
  showCancelButton = true,
  showAvailableLabel = true,
  showMyAssignments = true,
  showRouteAvailabilityBar = true,
  showRouteAssignedStatusBar = false,
  showCarrierRouteStatusBar = false,
  showFullyAssignedLoadBadge = true,
  showActionButton = true,
  onRouteDataUpdate,
}: LoadAcceptanceCardProps) => {
  const { language } = useLanguage();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const { formatRoute } = useRouteDisplay();
  const [completeCount, setCompleteCount] = useState(1);
  const [cancelCount, setCancelCount] = useState(1);
  const [actionMode, setActionMode] = useState<"complete" | "cancel" | null>(null);
  const [existingAcceptances, setExistingAcceptances] = useState<AcceptanceRecord[]>([]);
  const [internalLoading, setInternalLoading] = useState(false);
  const [completeLoading, setCompleteLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [carrierActiveTrucks, setCarrierActiveTrucks] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  
  // Reset counters when action mode changes to complete/cancel
  useEffect(() => {
    if (actionMode === "complete") {
      setCompleteCount(Math.min(1, carrierActiveTrucks));
    } else if (actionMode === "cancel") {
      setCancelCount(Math.min(1, carrierActiveTrucks));
    }
  }, [actionMode, carrierActiveTrucks]);
  
  // Initial loading effect - wait for user assignments to load before showing buttons
  useEffect(() => {
    if (!user) {
      setIsDataLoaded(true); // No user, no need to fetch assignments
      return;
    }
    
    setIsDataLoaded(false); // Start loading
    fetchUserAssignments();
  }, [user, id]);

  // Set up real-time subscription for load routes changes
  useEffect(() => {
    if (!user || !id) return;

    console.log('Setting up real-time subscription for load route:', id);

    const channel = supabase
      .channel(`load-route-${id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'load_routes',
          filter: `id=eq.${id}`
        },
        (payload) => {
          console.log('Real-time load route change detected:', payload);
          console.log('Route updated - refetching user assignments');
          
          // Refetch assignments when route data changes (which happens when anyone accepts/cancels)
          fetchUserAssignments();
          
          // Also notify parent component to refresh route data (like availableTrucks)
          if (onRouteDataUpdate) {
            onRouteDataUpdate();
          }
        }
      )
      .subscribe((status) => {
        console.log('Real-time subscription status:', status);
      });

    return () => {
      console.log('Cleaning up real-time subscription for route:', id);
      supabase.removeChannel(channel);
    };
  }, [user, id]);

  // Auto-adjust selected trucks if availability changes while selecting
  useEffect(() => {
    if (currentResponse?.status === "selecting") {
      if (availableTrucks === 0) {
        // If no trucks available, exit selection mode entirely
        onCancelSelection(id);
      } else if (currentResponse.trucksCommitted > availableTrucks) {
        // If selected count exceeds availability, adjust to max available
        onUpdateTrucks(id, availableTrucks);
      }
    }
  }, [availableTrucks, currentResponse, id, onUpdateTrucks, onCancelSelection]);

  const fetchUserAssignments = async () => {
    if (!user) return;
    
    console.log('fetchUserAssignments called for route:', id);
    
    try {
      // Get user's organizations
      const { data: userOrgs } = await supabase
        .from('organization_members')
        .select('org_id')
        .eq('user_id', user.id);
      
      const orgIds = userOrgs?.map(org => org.org_id) || [];
      
      if (orgIds.length === 0) return;

      // Fetch assignments for user's organizations for this specific route
      const { data: assignments, error } = await supabase
        .from('load_assignments')
        .select(`
          id,
          load_route_id,
          vehicles_accepted,
          vehicles_assigned,
          vehicles_active,
          assigned_at,
          status
        `)
        .in('carrier_org_id', orgIds)
        .eq('load_route_id', id);

      if (error) {
        console.error('Error fetching user assignments:', error);
        return;
      }

      console.log('Raw assignments from DB:', assignments);
      
      const mappedAcceptances = (assignments || []).map(assignment => ({
        id: assignment.id,
        trucksAccepted: assignment.vehicles_assigned || 0,
        timestamp: new Date(assignment.assigned_at),
        status: assignment.status
      }));

      // Calculate total active trucks for this carrier across all assignments on this route
      const totalActiveTrucks = (assignments || []).reduce((sum, assignment) => {
        return sum + (assignment.vehicles_active || 0);
      }, 0);

      console.log('Mapped acceptances:', mappedAcceptances);
      console.log('Total carrier active trucks:', totalActiveTrucks);
      setExistingAcceptances(mappedAcceptances);
      setCarrierActiveTrucks(totalActiveTrucks);
      setIsDataLoaded(true); // Mark data as loaded
    } catch (error) {
      console.error('Error fetching user assignments:', error);
      setIsDataLoaded(true); // Set as loaded even on error to prevent infinite loading
    }
  };

  // Atomic acceptance function to prevent race conditions
  const handleAtomicAcceptance = async (trucksRequested: number) => {
    if (!user || internalLoading) return;

    setInternalLoading(true);

    try {
      // Get user's organization
      const { data: userOrgs } = await supabase.rpc('get_user_organizations', { 
        user_uuid: user.id 
      });
      
      if (!userOrgs || userOrgs.length === 0) {
        throw new Error("Please create or join an organization first");
      }
      
      // Use atomic function to prevent race conditions
      const { data: result, error } = await supabase.rpc('atomic_truck_acceptance', {
        route_id_param: id,
        carrier_org_id_param: userOrgs[0].organization_id,
        assigned_by_param: user.id,
        trucks_requested: trucksRequested
      });

      if (error) throw error;

      // Cast and handle the atomic response
      const atomicResult = result as unknown as AtomicAcceptanceResponse;
      
      if (!atomicResult.success) {
        // Track failed acceptance attempt
        Sentry.captureException(new Error(`Load acceptance failed: ${atomicResult.error}`), {
          tags: {
            operation: 'accept_load',
            critical: true,
            business_process: 'load_acceptance'
          },
          extra: {
            routeId: id,
            trucksRequested,
            trucksAvailable: atomicResult.trucks_available,
            partialAvailable: atomicResult.partial_available,
            userId: user.id
          }
        });
        
        if (atomicResult.partial_available && atomicResult.partial_available > 0) {
          // Offer partial assignment
          toast({
            title: "Partial Availability",
            description: `${atomicResult.error}. Would you like to accept ${atomicResult.partial_available} trucks instead?`,
            variant: "default",
          });
          return false;
        } else {
          // No trucks available
          toast({
            title: "Booking Failed",
            description: atomicResult.error || "No trucks available",
            variant: "destructive",
          });
          return false;
        }
      }

      // Success - refresh assignments and show success message
      await fetchUserAssignments();
      
      // Track successful load acceptance
      Sentry.addBreadcrumb({
        message: `Successfully accepted ${atomicResult.trucks_assigned || trucksRequested} trucks`,
        category: 'business_success',
        level: 'info',
        data: { routeId: id, trucksAccepted: atomicResult.trucks_assigned || trucksRequested }
      });
      
      // Notification will be automatically queued by database trigger
      
      // Notify parent to refresh route data
      if (onRouteDataUpdate) {
        onRouteDataUpdate();
      }

      const isAdditional = hasAcceptances;
      toast({
        title: isAdditional ? "Additional Trucks Confirmed!" : "Route Confirmed!",
        description: `${atomicResult.trucks_assigned || trucksRequested} ${isAdditional ? 'additional ' : ''}truck(s) confirmed for ${formatRoute(loadDetail.pickupCity, loadDetail.dropCity)}`,
      });
      
      return true;
    } catch (error) {
      console.error('Error confirming acceptance:', error);
      
      // Capture critical acceptance error
      Sentry.captureException(error, {
        tags: {
          operation: 'accept_load',
          critical: true,
          business_process: 'load_acceptance',
          errorType: 'unexpected'
        },
        extra: {
          routeId: id,
          trucksRequested,
          userId: user.id
        }
      });
      
      toast({
        title: "Error",
        description: "Failed to confirm acceptance. Please try again.",
        variant: "destructive",
      });
      return false;
    } finally {
      setInternalLoading(false);
    }
  };
  
  // Internal complete/cancel handlers when no external handlers provided
  const handleCompleteLoadInternal = async (count: number) => {
    if (!user?.id || !existingAcceptances.length) return;
    
    try {
      // Use any assignment ID since backend handles multi-assignment logic
      const assignmentId = existingAcceptances[0].id;
      
      const { error } = await supabase.rpc('handle_truck_operation', {
        assignment_id_param: assignmentId,
        operation_type: 'complete',
        truck_count: count
      });

      if (error) throw error;
      
      toast({
        title: "Load Completed",
        description: `Successfully completed ${count} truck(s)`
      });
      
      // Refresh assignments
      await fetchUserAssignments();
    } catch (error: any) {
      console.error('Error completing load:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to complete load",
        variant: "destructive"
      });
    }
  };

  const handleCancelTrucksInternal = async (count: number) => {
    if (!user?.id || !existingAcceptances.length) return;
    
    try {
      // Use any assignment ID since backend handles multi-assignment logic
      const assignmentId = existingAcceptances[0].id;
      
      const { error } = await supabase.rpc('handle_truck_operation', {
        assignment_id_param: assignmentId,
        operation_type: 'cancel',
        truck_count: count
      });

      if (error) throw error;
      
      toast({
        title: "Load Cancelled",
        description: `Successfully cancelled ${count} truck(s)`
      });
      
      // Refresh assignments
      await fetchUserAssignments();
    } catch (error: any) {
      console.error('Error canceling trucks:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to cancel load",
        variant: "destructive"
      });
    }
  };

  const hasAcceptances = existingAcceptances.length > 0;
  const isSelecting = currentResponse?.status === "selecting";
  const isAccepted = currentResponse?.status === "accepted";

  console.log('LoadAcceptanceCard - existingAcceptances:', existingAcceptances);
  console.log('LoadAcceptanceCard - hasAcceptances:', hasAcceptances);
  console.log('LoadAcceptanceCard - context:', context);

  return (
    <Card className={`shadow-[var(--shadow-card)] w-full max-w-none sm:max-w-lg md:max-w-2xl lg:max-w-4xl`}>
      <CardHeader className="pb-6 px-6 pt-6">
        <div className="flex items-start md:items-center justify-between gap-3 md:gap-2">
          <div>
            <CardTitle className="text-lg">{formatRoute(loadDetail.pickupCity, loadDetail.dropCity)}</CardTitle>
            <div className="flex items-center gap-1 text-xs md:text-sm text-muted-foreground">
              <span>{id.substring(0, 8).toUpperCase()}</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            
            {showAvailableLabel && ((context === "accept-load" && availableTrucks > 0) || (context === "view-order" && availableTrucks > 0)) && (
              <div className="flex items-center gap-1 px-2 py-1 bg-green-500/10 rounded-md animate-[pulse_1s_ease-in-out_infinite] border border-green-500/20">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce shadow-lg shadow-green-500/50"></div>
                <span className="text-sm font-medium text-green-700 animate-[pulse_1s_ease-in-out_infinite]">{availableTrucks} {t('loadDetails.availability')}</span>
              </div>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6 px-6 pb-6">
        {/* Existing Bookings Compact Indicator */}
        {showMyAssignments && hasAcceptances && (
          <div className="space-y-2">
            <div 
              className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800 cursor-pointer hover:bg-green-100 dark:hover:bg-green-950/50 transition-colors"
              onClick={onToggleExpandedAcceptances}
            >
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-4 w-4 text-white" />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-green-800 dark:text-green-200">
                    {t('loadDetails.youHaveTrucksBooked', { count: existingAcceptances.reduce((sum, acc) => sum + acc.trucksAccepted, 0) })}
                  </span>
                  {carrierActiveTrucks > 0 && (
                    <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                      {t('loadDetails.activeTrucks', { count: carrierActiveTrucks })}
                    </span>
                  )}
                </div>
              </div>
              {onToggleExpandedAcceptances && (
                <Button variant="ghost" size="sm" className="text-green-600 hover:text-green-700 hidden md:block">
                  {showExpandedAcceptances ? t('loadDetails.hideDetails') : t('loadDetails.viewDetails')}
                </Button>
              )}
            </div>

            {showExpandedAcceptances && (
              <div className="p-4 bg-green-50/50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800 animate-fade-in">
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-green-900 dark:text-green-100 mb-3">{t('loadDetails.bookingHistory')}</h4>
                  {existingAcceptances
                    .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
                    .map((acc) => (
                    <div key={acc.id} className="flex items-center justify-between p-3 bg-white/60 dark:bg-black/20 rounded-lg border border-green-100 dark:border-green-700">
                      <div className="flex items-center gap-3">
                        <Truck className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-medium text-green-800 dark:text-green-200">
                          {t('loadDetails.trucksCount', { count: acc.trucksAccepted })}
                        </span>
                        <span className="text-sm text-green-600 dark:text-green-400">
                          • {formatAppDate(acc.timestamp.toISOString(), language)}
                        </span>
                      </div>
                      <Badge className="bg-green-100 text-green-800 border-green-300 dark:bg-green-900 dark:text-green-200">
                        {t('loadDetails.confirmed')}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Load Details */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="space-y-2 hidden md:block">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4" />
              {t('loadDetails.route')}
            </div>
            <p className="text-sm font-medium">{formatRoute(loadDetail.pickupCity, loadDetail.dropCity)}</p>
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              {t('loadDetails.pickupDate')}
            </div>
            <p className="font-medium text-sm">{formatAppDate(loadDetail.pickupDate, language)}</p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Truck className="h-4 w-4" />
              {t('loadDetails.vehicle')}
            </div>
            <Badge variant="outline" className="w-fit">{loadDetail.vehicleType}</Badge>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Weight className="h-4 w-4" />
              {t('loadDetails.cargo')}
            </div>
            <p className="text-sm font-medium">{loadDetail.weightTons} tons • {loadDetail.cargo_desc || 'No description'}</p>
          </div>
        </div>

        {/* Additional details (distance, duration, etc.) */}
        {additionalDetails}

        {/* Truck Capacity Progress - Different display based on context */}
        {(showRouteAvailabilityBar || showRouteAssignedStatusBar) && (
        <div className={`space-y-3 p-4 rounded-lg ${context === "accept-load" ? "bg-gradient-accent/10 border border-accent/20" : "bg-muted/30"}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Truck className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">
                {context === "view-order" ? "Truck Status" : t('loadDetails.availability')}
              </span>
            </div>
            
            {/* Show different counters based on context */}
            <span className="text-sm font-medium">
              {showRouteAvailabilityBar ? (
                // Route level: assigned/needed vehicles
                <>
                  <span className="text-green-600">{assignedTrucks}</span>
                  <span className="text-muted-foreground"> / {totalTrucks}</span>
                </>
              ) : (
                // Carrier level: completed/assigned for this carrier
                <>
                  <span className="text-green-600">{trucksCompleted}</span>
                  <span className="text-muted-foreground"> / {assignedTrucks}</span>
                </>
              )}
            </span>
          </div>
          
          {/* Additional truck status breakdown for view-order context */}
          {context === "view-order" && (assignedTrucks > 0 || trucksCompleted > 0 || trucksCancelled > 0) && (
            <div className="flex items-center gap-3 text-sm font-medium mt-2">
              {trucksCompleted > 0 && (
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-green-600">completed {trucksCompleted}</span>
                </span>
              )}
              {trucksCancelled > 0 && (
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span className="text-red-600">cancelled {trucksCancelled}</span>
                </span>
              )}
              {carrierActiveTrucks > 0 && (
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span className="text-blue-600">active {carrierActiveTrucks}</span>
                </span>
              )}
            </div>
          )}
          
          {/* Progress Bar */}
          {(showRouteAvailabilityBar && context === "accept-load") || (showRouteAssignedStatusBar && context === "view-order") ? (
            context === "accept-load" ? (
              /* Progress bar showing assigned trucks and preview of selecting trucks */
              <div className="w-full bg-muted h-3 rounded-full overflow-hidden flex">
                {/* Already assigned trucks */}
                <div 
                  className="h-full transition-all duration-500"
                  style={{ 
                    background: 'var(--gradient-accent)',
                    width: `${(assignedTrucks / totalTrucks) * 100}%`
                  }}
                />
                {/* Currently selecting trucks (preview only, not additive) */}
                {currentResponse?.trucksCommitted && !isAccepted && (
                  <div 
                    className="h-full transition-all duration-300 opacity-60 bg-gradient-to-r from-accent-green to-accent-blue"
                    style={{ 
                      width: `${(currentResponse.trucksCommitted / totalTrucks) * 100}%`
                    }}
                  />
                )}
              </div>
            ) : (
              /* Segmented bar for view order context - Show carrier's specific trucks */
              (() => {
                // Use carrier's specific data from assignments, not route-level data
                const carrierCompletedTrucks = existingAcceptances.reduce((sum, acc) => sum + acc.trucksAccepted, 0) - carrierActiveTrucks;
                const carrierTotalForProgress = carrierActiveTrucks + carrierCompletedTrucks;
                
                return (
                  <div className="w-full bg-muted h-3 rounded-full overflow-hidden flex">
                    {/* Completed trucks (green) */}
                    {carrierCompletedTrucks > 0 && (
                      <div 
                        className="h-full bg-green-500 transition-all duration-300"
                        style={{ width: `${(carrierCompletedTrucks / carrierTotalForProgress) * 100}%` }}
                      />
                    )}
                    
                    {/* Active trucks (blue) */}
                    {carrierActiveTrucks > 0 && (
                      <div 
                        className="h-full bg-blue-500 transition-all duration-300"
                        style={{ width: `${(carrierActiveTrucks / carrierTotalForProgress) * 100}%` }}
                      />
                    )}
                  </div>
                );
              })()
            )
          ) : null}
          
          {/* Summary text - only show for carriers with actual assignments */}
          {context !== "accept-load" && (assignedTrucks > 0 || trucksCompleted > 0 || trucksCancelled > 0) && null}
        </div>
        )}

        {/* Carrier Route Status Bar - Show carrier's specific truck status */}
        {showCarrierRouteStatusBar && hasAcceptances && (
          <div className={`space-y-3 p-4 rounded-lg bg-gradient-accent/10 border border-accent/20`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">
                  Your Assignment Progress
                </span>
              </div>
              
              {(() => {
                const carrierAssignedTrucks = existingAcceptances.reduce((sum, acc) => sum + acc.trucksAccepted, 0);
                const carrierCompletedTrucks = carrierAssignedTrucks - carrierActiveTrucks; // Calculate completed from database
                const activePlusCompleted = carrierActiveTrucks + carrierCompletedTrucks; // Only active + completed for display
                
                return (
                  <span className="text-sm font-medium">
                    <span className="text-blue-600">{activePlusCompleted}</span>
                    <span className="text-muted-foreground"> / {activePlusCompleted} progress</span>
                  </span>
                );
              })()}
            </div>
            
            {/* Additional truck status breakdown */}
            {(() => {
              const carrierAssignedTrucks = existingAcceptances.reduce((sum, acc) => sum + acc.trucksAccepted, 0);
              // For now, calculate completed as assigned minus active
              const carrierCompletedTrucks = carrierAssignedTrucks - carrierActiveTrucks;
              // We need to get cancelled trucks from assignment data separately
              const carrierCancelledTrucks = 0; // TODO: Get from actual assignment data
              
              return carrierAssignedTrucks > 0 && (carrierActiveTrucks > 0 || carrierCompletedTrucks > 0 || carrierCancelledTrucks > 0) && (
                <div className="flex items-center gap-3 text-sm font-medium mt-2">
                  {carrierActiveTrucks > 0 && (
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      <span className="text-blue-600">{carrierActiveTrucks} active</span>
                    </span>
                  )}
                  {carrierCompletedTrucks > 0 && (
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <span className="text-green-600">{carrierCompletedTrucks} completed</span>
                    </span>
                  )}
                  {carrierCancelledTrucks > 0 && (
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <span className="text-red-600">{carrierCancelledTrucks} cancelled</span>
                    </span>
                  )}
                </div>
              );
            })()}
            
            {/* Progress Bar */}
            {(() => {
              const carrierAssignedTrucks = existingAcceptances.reduce((sum, acc) => sum + acc.trucksAccepted, 0);
              const carrierCompletedTrucks = carrierAssignedTrucks - carrierActiveTrucks; // Calculate completed from database
              const activePlusCompleted = carrierActiveTrucks + carrierCompletedTrucks; // Only use active + completed for progress bar
              
              return (
                <div className="w-full bg-muted h-3 rounded-full overflow-hidden flex">
                  {/* Completed trucks (green) */}
                  {carrierCompletedTrucks > 0 && (
                    <div 
                      className="h-full bg-green-500 transition-all duration-300"
                      style={{ width: `${(carrierCompletedTrucks / activePlusCompleted) * 100}%` }}
                    />
                  )}
                  
                  {/* Active trucks (blue) */}
                  {carrierActiveTrucks > 0 && (
                    <div 
                      className="h-full bg-blue-500 transition-all duration-300"
                      style={{ width: `${(carrierActiveTrucks / activePlusCompleted) * 100}%` }}
                    />
                  )}
                </div>
              );
            })()}
            
          </div>
        )}

        {/* Truck Selection (show only when actively selecting) */}
        {isSelecting && (
          <div className="border-t pt-4">
            <Label className="text-sm font-medium">{t('loadDetails.trucksToCommit')}</Label>
            <div className="flex items-center gap-4 mt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onUpdateTrucks(id, (currentResponse?.trucksCommitted || 1) - 1)}
                disabled={!currentResponse?.trucksCommitted || currentResponse.trucksCommitted <= 1}
              >
                <Minus className="h-4 w-4" />
              </Button>
              
              <Input
                type="number"
                min="1"
                max={availableTrucks}
                value={currentResponse?.trucksCommitted || 1}
                onChange={(e) => onUpdateTrucks(id, Math.min(availableTrucks, parseInt(e.target.value) || 1))}
                className="w-20 text-center font-bold"
              />
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => onUpdateTrucks(id, (currentResponse?.trucksCommitted || 1) + 1)}
                disabled={availableTrucks <= 0 || (currentResponse?.trucksCommitted || 1) >= availableTrucks}
              >
                <Plus className="h-4 w-4" />
              </Button>
              
              <span className="text-sm text-muted-foreground">
                {t('loadDetails.maxAvailable', { count: availableTrucks })}
              </span>
            </div>

            {isSelecting && (
              <div className="flex gap-3 mt-4">
                <Button 
                  type="button"
                  onClick={async (e) => {
                    e.preventDefault();
                    const trucksToAccept = currentResponse?.trucksCommitted || 1;
                    
                    // Always use atomic function to prevent race conditions
                    const success = await handleAtomicAcceptance(trucksToAccept);
                    if (success) {
                      // Clear the selection state after successful acceptance
                      onCancelSelection(id);
                    }
                  }}
                  disabled={isLoadingAccept || internalLoading}
                  className="flex-1 text-white"
                  style={{ background: 'var(--gradient-accent)' }}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {(isLoadingAccept || internalLoading) ? t('loadDetails.processing') : t('loadDetails.confirmAcceptance')}
                </Button>
                
                <Button 
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    onCancelSelection(id);
                  }}
                  variant="outline" 
                  className="flex-1"
                >
                  {t('loadDetails.cancel')}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons Section - Show skeleton until data loads */}
        {!isDataLoaded ? (
          <div className="border-t pt-4">
            <Skeleton className="h-12 w-full rounded-md" />
          </div>
        ) : (
          <>
            {/* Primary Accept Button - Only when carrier has no active trucks and route has available trucks */}
            {showAcceptButton && !isSelecting && availableTrucks > 0 && actionMode === null && !(showActionButton && carrierActiveTrucks > 0) && (
              <div className="border-t pt-4">
                <Button 
                  type="button"
                  onClick={async (e) => {
                    e.preventDefault();
                    setAcceptLoading(true);
                    try {
                      onStartSelection(id);
                    } finally {
                      setAcceptLoading(false);
                    }
                  }}
                  disabled={acceptLoading}
                  className="w-full h-12 text-white disabled:opacity-50 disabled:cursor-not-allowed font-medium text-base shadow-lg hover:shadow-xl transition-all duration-200"
                  style={{ background: 'var(--gradient-accent)' }}
                >
                  {acceptLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                      Starting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      {hasAcceptances ? t('loadDetails.acceptLoad') : t('loadDetails.acceptLoad')}
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Actions Button - Only when carrier has active trucks */}
            {showActionButton && carrierActiveTrucks > 0 && actionMode === null && !isSelecting && (
              <div className="border-t pt-4">
                <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="default"
                      size="lg"
                      className="w-full h-12 bg-gradient-to-r from-sky-400 via-blue-600 to-indigo-700 hover:from-sky-500 hover:via-blue-700 hover:to-indigo-800 text-white font-medium text-base shadow-lg hover:shadow-xl transition-all duration-200 border border-sky-400 hover:border-indigo-600"
                    >
                      <span>{t('loadDetails.actions')}</span>
                      <RefreshCw className="h-4 w-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent 
                    align="center" 
                    className="w-64 bg-white dark:bg-gray-800 border border-border shadow-xl z-50 p-2"
                  >
                    {/* Complete Trucks - only when carrier has active trucks */}
                    {showCompleteButton && carrierActiveTrucks > 0 && (
                      <DropdownMenuItem 
                        onClick={(e) => {
                          e.preventDefault();
                          setDropdownOpen(false);
                          setActionMode("complete");
                        }}
                        disabled={isLoadingComplete}
                        className="cursor-pointer hover:bg-green-50 dark:hover:bg-green-900/20 focus:bg-green-50 dark:focus:bg-green-900/20 rounded-md p-3 mb-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoadingComplete ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-green-600 border-t-transparent mr-3" />
                            <div className="flex flex-col">
                              <span className="text-green-700 dark:text-green-300 font-medium">{t('loadDetails.processing')}</span>
                              <span className="text-xs text-green-600/70 dark:text-green-400/70">{t('loadDetails.pleaseWait')}</span>
                            </div>
                          </>
                        ) : (
                          <>
                            <CheckCircle className="h-5 w-5 mr-3 text-green-600" />
                            <div className="flex flex-col">
                              <span className="text-green-700 dark:text-green-300 font-medium">{t('loadDetails.completeTrucks')}</span>
                              <span className="text-xs text-green-600/70 dark:text-green-400/70">{t('loadDetails.markAsDelivered')}</span>
                            </div>
                          </>
                        )}
                      </DropdownMenuItem>
                    )}
                    
                    {/* Cancel Trucks - only when carrier has active trucks */}
                    {showCancelButton && carrierActiveTrucks > 0 && (
                      <DropdownMenuItem 
                        onClick={(e) => {
                          e.preventDefault();
                          setDropdownOpen(false);
                          setActionMode("cancel");
                        }}
                        className="cursor-pointer hover:bg-red-50 dark:hover:bg-red-900/20 focus:bg-red-50 dark:focus:bg-red-900/20 rounded-md p-3 mb-1"
                      >
                        <XCircle className="h-5 w-5 mr-3 text-red-600" />
                        <div className="flex flex-col">
                          <span className="text-red-700 dark:text-red-300 font-medium">{t('loadDetails.cancelTrucks')}</span>
                          <span className="text-xs text-red-600/70 dark:text-red-400/70">{t('loadDetails.removeFromAssignment')}</span>
                        </div>
                      </DropdownMenuItem>
                    )}

                    {/* Accept New Loads - only when available trucks exist - moved to last */}
                    {showAcceptButton && availableTrucks > 0 && (
                      <DropdownMenuItem 
                        onClick={async (e) => {
                          e.preventDefault();
                          setDropdownOpen(false);
                          setAcceptLoading(true);
                          try {
                            onStartSelection(id);
                          } finally {
                            setAcceptLoading(false);
                          }
                        }}
                        disabled={acceptLoading}
                        className="cursor-pointer rounded-md p-3 text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ background: 'var(--gradient-accent)' }}
                      >
                        {acceptLoading ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3" />
                            <div className="flex flex-col">
                              <span className="text-white font-medium">{t('loadDetails.starting')}</span>
                              <span className="text-xs text-white/70">{t('loadDetails.pleaseWait')}</span>
                            </div>
                          </>
                        ) : (
                          <>
                            <Truck className="h-5 w-5 mr-3 text-white" />
                            <div className="flex flex-col">
                              <span className="text-white font-medium">{t('loadDetails.acceptNewLoads')}</span>
                              <span className="text-xs text-white/70">{t('loadDetails.trucksAvailable', { count: availableTrucks })}</span>
                            </div>
                          </>
                        )}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            {/* Confirmation Component for Complete Action */}
            {actionMode === "complete" && (
              <div className="border-t pt-4">
                <div className="space-y-3">
                  <Label className="text-sm font-medium">{t('loadDetails.trucksToComplete')}</Label>
                  <div className="flex items-center gap-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCompleteCount(Math.max(1, completeCount - 1))}
                      disabled={completeCount <= 1}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    
                    <Input
                      type="number"
                      min="1"
                      max={carrierActiveTrucks}
                      value={completeCount}
                      onChange={(e) => setCompleteCount(Math.min(carrierActiveTrucks, Math.max(1, parseInt(e.target.value) || 1)))}
                      className="w-20 text-center font-bold"
                    />
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCompleteCount(Math.min(carrierActiveTrucks, completeCount + 1))}
                      disabled={completeCount >= carrierActiveTrucks}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                    
                    <span className="text-sm text-muted-foreground">
                      {t('loadDetails.maxActive', { count: carrierActiveTrucks })}
                    </span>
                  </div>

                  <div className="flex gap-3">
                    <Button 
                      type="button"
                       onClick={async (e) => {
                         e.preventDefault();
                         setCompleteLoading(true);
                         try {
                           if (onComplete) {
                             await onComplete(id, completeCount);
                             await fetchUserAssignments();
                           } else {
                             await handleCompleteLoadInternal(completeCount);
                           }
                         } finally {
                           setCompleteLoading(false);
                         }
                         setActionMode(null);
                       }}
                      disabled={isLoadingComplete || completeLoading}
                      className="flex-1 text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {(isLoadingComplete || completeLoading) ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                          {t('loadDetails.processing')}
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          {t('loadDetails.completeTrucksButton', { count: completeCount })}
                        </>
                      )}
                    </Button>
                    
                    <Button 
                      onClick={() => setActionMode(null)} 
                      variant="outline" 
                      className="flex-1"
                    >
                      {t('loadDetails.cancel')}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Confirmation Component for Cancel Action */}
            {actionMode === "cancel" && (
              <div className="border-t pt-4">
                <div className="space-y-3">
                  <Label className="text-sm font-medium">{t('loadDetails.trucksToCancel')}</Label>
                  <div className="flex items-center gap-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCancelCount(Math.max(1, cancelCount - 1))}
                      disabled={cancelCount <= 1}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    
                    <Input
                      type="number"
                      min="1"
                      max={carrierActiveTrucks}
                      value={cancelCount}
                      onChange={(e) => setCancelCount(Math.min(carrierActiveTrucks, Math.max(1, parseInt(e.target.value) || 1)))}
                      className="w-20 text-center font-bold"
                    />
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCancelCount(Math.min(carrierActiveTrucks, cancelCount + 1))}
                      disabled={cancelCount >= carrierActiveTrucks}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                    
                    <span className="text-sm text-muted-foreground">
                      {t('loadDetails.maxActive', { count: carrierActiveTrucks })}
                    </span>
                  </div>

                  <div className="flex gap-3">
                    <Button 
                      type="button"
                       onClick={async (e) => {
                         e.preventDefault();
                         setCancelLoading(true);
                         try {
                           if (onCancel) {
                             await onCancel(id, cancelCount);
                             await fetchUserAssignments();
                           } else {
                             await handleCancelTrucksInternal(cancelCount);
                           }
                         } finally {
                           setCancelLoading(false);
                         }
                         setActionMode(null);
                       }}
                      disabled={isLoadingCancel || cancelLoading}
                      variant="destructive"
                      className="flex-1"
                    >
                      {(isLoadingCancel || cancelLoading) ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                          {t('loadDetails.processing')}
                        </>
                      ) : (
                        <>
                          <XCircle className="h-4 w-4 mr-2" />
                          {t('loadDetails.cancelTrucksButton', { count: cancelCount })}
                        </>
                      )}
                    </Button>
                    
                    <Button 
                      onClick={() => setActionMode(null)} 
                      variant="outline" 
                      className="flex-1"
                    >
                      {t('loadDetails.cancel')}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* No capacity available */}
            {showFullyAssignedLoadBadge && availableTrucks === 0 && !isSelecting && !isAccepted && !(showActionButton && carrierActiveTrucks > 0 && actionMode === null) && (
              <div className="text-center py-4 text-muted-foreground">
                <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
                <p>This load is fully assigned</p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
